/**
 * Edit Team Dialog
 * Form to update existing team with validation
 * Based on database schema: teams table
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEligibleTeamLeads } from '@/lib/hooks/useEmployees';
import { Loader2Icon } from 'lucide-react';
import type { Team } from '@repo/types';

interface EditTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TeamUpdateData) => void;
  team: Team;
}

export interface TeamUpdateData {
  name: string;
  leadId?: string;
}

export function EditTeamDialog({ open, onOpenChange, onSubmit, team }: EditTeamDialogProps) {
  const [formData, setFormData] = useState<TeamUpdateData>({
    name: team.name,
    leadId: team.leadId ?? undefined,
  });

  const [errors, setErrors] = useState<Partial<Record<keyof TeamUpdateData, string>>>({});

  // Fetch eligible team leads from API
  const { data: eligibleLeads = [], isLoading: isLoadingLeads } = useEligibleTeamLeads();

  // Update form when team prop changes
  useEffect(() => {
    setFormData({
      name: team.name,
      leadId: team.leadId ?? undefined,
    });
    setErrors({});
  }, [team]);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof TeamUpdateData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Team name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Team name must be at least 2 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      onSubmit(formData);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Edit Team</DialogTitle>
          <DialogDescription>
            Update team information. All fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Team Name */}
          <div className="space-y-2">
            <Label htmlFor="name">
              Team Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="name"
              placeholder="Engineering, Sales, Operations, etc."
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className={errors.name ? 'border-destructive' : ''}
            />
            {errors.name && <p className="text-sm text-destructive">{errors.name}</p>}
          </div>

          {/* Team Lead */}
          <div className="space-y-2">
            <Label htmlFor="lead">Team Lead (Optional)</Label>
            <Select
              value={formData.leadId || 'none'}
              onValueChange={(value) =>
                setFormData({ ...formData, leadId: value === 'none' ? undefined : value })
              }
              disabled={isLoadingLeads}
            >
              <SelectTrigger>
                <SelectValue placeholder={isLoadingLeads ? 'Loading...' : 'Select a team lead'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Team Lead</SelectItem>
                {isLoadingLeads ? (
                  <div className="flex items-center justify-center py-2">
                    <Loader2Icon className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  eligibleLeads.map((employee) => (
                    <SelectItem key={employee.id} value={employee.id}>
                      {employee.name} ({employee.role.replace('_', ' ')})
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Only employees with Team Lead, Manager, or Admin roles can be assigned as team leads.
            </p>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
